import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useFormik } from "formik";
import * as Yup from "yup";
import { toast } from "react-hot-toast";
import { setAuthModalOpen } from "../../store/features/authModalSlice";
import { setUser } from "../../store/features/userSlice";
import ImageUploadS3 from "./ImageUploadS3";
import userApi from "../../api/user/user.api";
import ProcessingBar from "../../Util/Processingbar";
import Loading from "../../Util/Loading";
import { motion, AnimatePresence } from "framer-motion";
import { fadeUp } from "animations/variants";
import { Eye, EyeClosed } from "lucide-react";

const SignupForm = ({ switchAuthState, actionState }) => {
  const dispatch = useDispatch();
  const [isLoginRequest, setIsLoginRequest] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [Images, setImages] = useState([]);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [operators, setOperators] = useState([]);
  const [selectedOperator, setSelectedOperator] = useState();

  const [isLoading, setIsLoading] = useState(true);

  const getOperators = async () => {
    try {
      const { response, err } = await userApi.getAllOperatorPublic();
      console.log({ response });

      if (response && response.data) {
        setOperators(response.data);
      } else {
        toast.error("No data found");
      }
    } catch (error) {
      toast.error("Something went wrong!");
      console.error("Error fetching data:", error);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      await Promise.all([getOperators()]);
      setIsLoading(false);
    };

    fetchData();
  }, []);

  console.log({ operators });

  const signupForm = useFormik({
    initialValues: {
      username: "",
      displayName: "",
      password: "",
      confirmPassword: "",
      operator: "68588c15576acce1c50cada9",
      photo: "",
      phone: "",
    },
    validationSchema: Yup.object({
      username: Yup.string()
        .min(3, "Username must be at least 3 characters")
        .required("Required"),
      displayName: Yup.string()
        .min(3, "Display name must be at least 3 characters")
        .required("Required"),

      phone: Yup.string()
        .min(8, "Phone number must be at least 8 digits")
        .required("Required"),
      password: Yup.string()
        .min(3, "Password must be at least 3 characters")
        .required("Required"),
      confirmPassword: Yup.string()
        .oneOf([Yup.ref("password")], "Passwords must match")
        .required("Required"),
    }),
    onSubmit: async (values) => {
      console.log("Form values with photo:", values);

      setErrorMessage("");
      setIsLoginRequest(true);

      try {
        const { response, err } = await userApi.signup(values);

        if (err) {
          throw new Error(err.message || "Sign up failed");
        }

        if (response) {
          signupForm.resetForm();
          dispatch(setUser(response.data));
          dispatch(setAuthModalOpen(false));
          toast.success("Sign up successful");
        }
      } catch (error) {
        console.error(error);
        setErrorMessage(error.message);
        toast.error(error.message);
      } finally {
        setIsLoginRequest(false);
      }
    },
  });

  const updateImages = (newImageUrl) => {
    setImages([newImageUrl]); // put it inside an array
    signupForm.setFieldValue("photo", newImageUrl || ""); // use the URL string
  };

  if (isLoading) {
    return <Loading />;
  }

  return (
    <motion.form
      onSubmit={signupForm.handleSubmit}
      className="space-y-4"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.4 }}
    >
      <input
        type="text"
        name="username"
        placeholder="E-mail"
        className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-yellowstar"
        onChange={(e) => {
          signupForm.setFieldValue("username", e.target.value.toLowerCase());
        }}
        onBlur={signupForm.handleBlur} // Required for Formik validation to trigger on blur
        value={signupForm.values.username}
      />

      {signupForm.touched.username && signupForm.errors.username && (
        <div className="text-red-500 text-sm mt-1">
          {signupForm.errors.username}
        </div>
      )}

      <input
        type="text"
        name="phone"
        placeholder="Phone Number"
        className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-yellowstar "
        onChange={signupForm.handleChange}
        value={signupForm.values.phone}
      />

      {signupForm.touched.phone && signupForm.errors.phone && (
        <div className="text-red-500 text-sm mt-1">
          {signupForm.errors.phone}
        </div>
      )}

      <input
        type="text"
        name="displayName"
        placeholder="Full Name"
        className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-yellowstar"
        onChange={signupForm.handleChange}
        value={signupForm.values.displayName}
      />
      {signupForm.touched.displayName && signupForm.errors.displayName && (
        <div className="text-red-500 text-sm mt-1">
          {signupForm.errors.displayName}
        </div>
      )}

      {signupForm.touched.operator && signupForm.errors.operator && (
        <div className="text-red-500 text-sm mt-1">
          {signupForm.errors.operator}
        </div>
      )}
      <div className="relative">
        <input
          type={showPassword ? "text" : "password"}
          name="password"
          placeholder="Password"
          className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-yellowstar"
          onChange={signupForm.handleChange}
          value={signupForm.values.password}
        />
        <button
          type="button"
          onClick={() => setShowPassword(!showPassword)}
          className="
      absolute right-3 top-1/2 -translate-y-1/2
      text-gray-500 hover:text-yellowstar
      transition-colors
    "
        >
          {showPassword ? <EyeClosed size={18} /> : <Eye size={18} />}
        </button>
      </div>

      {signupForm.touched.password && signupForm.errors.password && (
        <div className="text-red-500 text-sm mt-1">
          {signupForm.errors.password}
        </div>
      )}

      <div className="relative">
        <input
          type={showConfirmPassword ? "text" : "password"}
          name="confirmPassword"
          placeholder="Confirm Password"
          className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-yellowstar"
          onChange={signupForm.handleChange}
          value={signupForm.values.confirmPassword}
        />
        <button
          type="button"
          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
          className="
      absolute right-3 top-1/2 -translate-y-1/2
      text-gray-500 hover:text-yellowstar
      transition-colors
    "
        >
          {showConfirmPassword ? <EyeClosed size={18} /> : <Eye size={18} />}
        </button>
      </div>

      {signupForm.touched.confirmPassword &&
        signupForm.errors.confirmPassword && (
          <div className="text-red-500 text-sm mt-1">
            {signupForm.errors.confirmPassword}
          </div>
        )}

      <ImageUploadS3 refreshFunction={updateImages} />

      <motion.button
        type="submit"
        whileTap={{ scale: 0.95 }}
        whileHover={{ scale: 1.02 }}
        transition={{ type: "spring", stiffness: 300 }}
        className="w-full text-navy hover:text-white py-2 rounded-md transition duration-300 tracking-widest uppercase hover:shadow-lg"
        disabled={isLoginRequest}
        style={{
          background: "linear-gradient(-45deg, #fed693, #d09929)",
        }}
      >
        <AnimatePresence mode="wait">
          {isLoginRequest ? (
            <motion.div
              key="loading"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <ProcessingBar />
            </motion.div>
          ) : (
            <motion.span
              key="text"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              Sign Up
            </motion.span>
          )}
        </AnimatePresence>
      </motion.button>

      {/* Divider */}
      <motion.div
        variants={fadeUp}
        className="py-3 flex items-center justify-center gap-4"
      >
        <div className="w-20 border-t border-dashed border-gray-300"></div>
        <span className="w-2 h-2 bg-neonYellow rounded-full"></span>
        <span className="w-2 h-2 bg-neonYellow rounded-full"></span>
        <span className="w-2 h-2 bg-neonYellow rounded-full"></span>
        <div className="w-20 border-t border-dashed border-gray-300"></div>
      </motion.div>

      <button
        type="button"
        onClick={() => switchAuthState(actionState.signin)}
        className="
    w-full mt-4 py-2
    tracking-widest uppercase
    border-b border-navy border-dotted
    text-navy
    transition-all duration-300
    hover:bg-gradient-to-r
    hover:from-[#d09929] hover:to-[#fed693]
    hover:text-white
    hover:border-transparent
    hover:shadow-lg
  "
      >
        Sign In
      </button>

      {errorMessage && (
        <p className="text-red-500 text-sm text-center">{errorMessage}</p>
      )}
    </motion.form>
  );
};

export default SignupForm;
